/**
 * The lead filter set, laid out the way the owner's LeadByte screen is: four
 * pickers that are always there, and the rest behind Advanced filters.
 *
 * The panel edits a draft. Nothing here changes the table until Search is
 * pressed (or Enter in a text box), so picking five things costs one query
 * rather than five, and the table never shows a half-built search.
 *
 * Every dropdown's options come from `GET /api/v1/meta/enums` or from live
 * data, never from a list written here. The Alpine builder hard-coded its
 * vocabularies in the markup and they drifted from what the engine implements.
 */
import {
  ActionIcon, Autocomplete, Button, Collapse, Group, Indicator, Input, MultiSelect, NumberInput, Popover, Select,
  SimpleGrid, Stack, Switch, Text, TextInput, Tooltip, UnstyledButton,
} from '@mantine/core';
import {
  IconAdjustmentsHorizontal, IconCalendar, IconChevronDown, IconCopy, IconPlus, IconRefresh,
  IconInfoCircle, IconSearch, IconTrash,
} from '@tabler/icons-react';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { useRef, useState, type KeyboardEvent } from 'react';
import type { LeadFilters } from '../api/queries';
import { useVocab } from '../vocab';
import {
  useCampaigns, useDestinations, useEntities, useEnums, useImports, useLeadFacets,
} from '../api/queries';
import {
  CUSTOM, DATE_PRESETS, VALUELESS_OPERATORS, campaignOptions, customDatePatch, dateRangeLabel,
  dateRangePatch, dateSelectValue, groupByActive, ruleFieldOptions, splitDateTime,
  supplierOptions,
} from './leadsFilterModel';
import { PHONE_STATUS_LABELS } from './phoneStatus';
import { useProperties, type PropertyDef } from '../api/properties';
import { useLeadFields, useLeadFieldValues } from '../api/leadFields';
import { builtInLabel, coverageHint, filterFieldOptions } from './SmartFilterEditor';
import { FilterValue } from './properties/FilterValue';
import { operatorLabel, operatorsFor } from './properties/propertyModel';
import { useScoringSettings } from '../api/scoring';
import { useScoringInUse } from './scoring/scoringShared';

const HLR_STATUSES = Object.entries(PHONE_STATUS_LABELS).map(([value, label]) => ({ value, label }));

/**
 * The old app's filter controls are 32px tall with 13px text. Mantine's `xs`
 * gives the right height but drops the type to 11px, smaller than anything
 * else on the page; `sm` gives readable text at 36px, which reads as a form to
 * fill in rather than a toolbar to skim. Take the height from one and the type
 * from the other.
 */
const COMPACT = {
  size: 'xs' as const,
  styles: { input: { fontSize: '0.8125rem' } },
};

/**
 * COMPACT for the multi-selects, plus one line of chips at most.
 *
 * Left to wrap, picking a value pushed the placeholder onto a second line,
 * the box doubled in height and the toolbar's other controls narrowed to
 * make room. Chips now stay on one line, clipped at the edge, so the box
 * keeps its size and the full selection is still shown in the dropdown.
 */
const COMPACT_MULTI = {
  size: 'xs' as const,
  styles: {
    input: { fontSize: '0.8125rem' },
    pillsList: { flexWrap: 'nowrap' as const, overflow: 'hidden' },
    inputField: { minWidth: '3ch' },
  },
};

/**
 * A field label with its explanation behind an info icon.
 *
 * As a description line under the label, the explanation pushed that field's
 * box below its neighbours, so a row of filters no longer lined up.
 */
function InfoLabel({ children, help }: { children: React.ReactNode; help: string }) {
  return (
    <Group gap={4} wrap="nowrap" component="span" style={{ display: 'inline-flex' }}>
      {children}
      <Tooltip label={help} withArrow multiline maw={260}>
        <IconInfoCircle size={13} aria-hidden style={{ opacity: 0.6, cursor: 'help' }} />
      </Tooltip>
    </Group>
  );
}

/**
 * How one import reads in the picker.
 *
 * The file name alone does not identify it: the same file gets re-uploaded
 * after a failure, and several campaigns import files called `leads.csv`. The
 * date is what tells two apart, and the row count is what tells you which of
 * the two you meant.
 */
function importLabel(job: {
  file_name: string;
  created_at: string;
  rows_imported?: number | null;
}): string {
  const when = new Date(job.created_at);
  const date = Number.isNaN(when.getTime())
    ? job.created_at
    : when.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' });
  const rows = job.rows_imported ?? null;
  return rows === null
    ? `${job.file_name} · ${date}`
    : `${job.file_name} · ${date} · ${rows.toLocaleString()} rows`;
}

/** `[]` reads as "no filter" on the server, but `null` keeps saved tabs tidy. */
const listOrNull = (v: string[]) => (v.length ? v : null);

/**
 * The date and time control: presets, or a custom range with times.
 *
 * One control rather than a preset dropdown plus loose From and To boxes,
 * which is how the old panel ended up searching a range nobody chose.
 */
function DateRangeControl({
  filters,
  onChange,
  onEnter,
}: {
  filters: LeadFilters;
  onChange: (patch: LeadFilters) => void;
  onEnter: (e: KeyboardEvent) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = dateSelectValue(filters);
  const from = splitDateTime(filters.date_from, false);
  const to = splitDateTime(filters.date_to, true);

  const pick = (value: string | null) => {
    onChange(dateRangePatch(value, filters));
    if (value !== CUSTOM) setOpen(false);
  };

  const option = (value: string | null, label: string) => (
    <UnstyledButton
      key={label}
      onClick={() => pick(value)}
      px="xs"
      py={4}
      style={{
        borderRadius: 'var(--mantine-radius-sm)',
        fontSize: '0.8125rem',
        fontWeight: selected === value ? 600 : 400,
        background: selected === value ? 'var(--mantine-color-default-hover)' : undefined,
      }}
    >
      {label}
    </UnstyledButton>
  );

  return (
    <Popover opened={open} onChange={setOpen} position="bottom-start" shadow="md" withinPortal>
      <Popover.Target>
        <Input
          {...COMPACT}
          component="button"
          type="button"
          pointer
          aria-label="Date and time"
          leftSection={<IconCalendar size={14} />}
          rightSection={<IconChevronDown size={14} />}
          rightSectionPointerEvents="none"
          onClick={() => setOpen((o) => !o)}
        >
          <Text span size="0.8125rem" c={selected ? undefined : 'dimmed'} truncate>
            {dateRangeLabel(filters)}
          </Text>
        </Input>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Stack gap={2} miw={260}>
          {option(null, 'Any time')}
          {DATE_PRESETS.map((p) => option(p.value, p.label))}
          {option(CUSTOM, 'Custom range')}
          {selected === CUSTOM && (
            <Stack gap="xs" pt="xs">
              {(
                [
                  ['from', 'From', from],
                  ['to', 'To', to],
                ] as const
              ).map(([which, label, v]) => (
                <Group key={which} gap="xs" wrap="nowrap" align="flex-end">
                  <TextInput
                    {...COMPACT}
                    label={label}
                    type="date"
                    value={v.date}
                    onChange={(e) => onChange(customDatePatch(which, e.currentTarget.value, v.time))}
                    onKeyDown={onEnter}
                  />
                  <TextInput
                    {...COMPACT}
                    aria-label={`${label} time`}
                    type="time"
                    step={1}
                    value={v.time}
                    disabled={!v.date}
                    onChange={(e) => onChange(customDatePatch(which, v.date, e.currentTarget.value))}
                    onKeyDown={onEnter}
                  />
                </Group>
              ))}
              <Text size="xs" c="dimmed">UK time.</Text>
            </Stack>
          )}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * Rows of field, condition and value, mapped onto `field_filters`.
 *
 * Conditions are the `field` filter type's operators from the enums, the same
 * list the distribution engine implements, so a condition that can be built
 * here is one the server can evaluate.
 *
 * The fields are the ones leads of the selected campaigns carry (every
 * campaign when none is selected, or when the rules ignore the campaign
 * filter), from `GET /api/v1/lead-fields`, the same list the smart-filter
 * field picker offers. The server reads a custom key from either storage
 * shape of any campaign, so any number of campaigns can be selected.
 */
export function RuleBuilder({
  filters,
  onChange,
  onEnter,
  enabled,
}: {
  filters: LeadFilters;
  onChange: (patch: LeadFilters) => void;
  onEnter: (e: KeyboardEvent) => void;
  /** Read the fields only once Advanced filters is open. */
  enabled: boolean;
}) {
  const v = useVocab();
  const enums = useEnums();
  const ignoreCampaigns = filters.ignore_campaign_filter ?? false;
  const fieldCampaigns = ignoreCampaigns ? [] : (filters.campaign_ids ?? []);
  const seen = useLeadFields(fieldCampaigns, enabled).data;
  const rows = filters.field_filters ?? [];

  const operators =
    enums.data?.filter_types
      .find((t) => t.value === 'field')
      ?.options.find((o) => o.key === 'operator')
      ?.values.map((v) => ({ value: v.value, label: v.label })) ?? [];
  // Lead properties, defined once for the organisation: they label a key
  // and give it the input its type needs.
  const props = useProperties('submission', { enabled }).data?.properties ?? [];
  const defOf = (field: string) => props.find((d) => d.key === field) ?? null;
  /** The conditions for a field: its type's, keeping one already chosen. */
  const operatorsOf = (field: string, current: string) => {
    const def = defOf(field);
    const allowed = operatorsFor(def?.type, operators.map((o) => o.value));
    return operators
      .filter((o) => allowed.includes(o.value) || o.value === current)
      .map((o) => ({ value: o.value, label: operatorLabel(def?.type, o.value, o.label) }));
  };

  // The standard lead fields are the ones an import can map onto. The
  // received date is left out: it has its own control above.
  const standard = (enums.data?.import_fields ?? [])
    .filter((f) => f.value !== 'created_at')
    .map((f) => ({ value: f.value, label: f.label }));

  const fieldOptions = ruleFieldOptions({
    standard,
    fields: seen?.fields,
    labels: props.map((d) => ({ value: d.key, label: d.label })),
    builtInLabel: (key) => builtInLabel(v, key),
  }).filter((g) => g.items.length > 0);
  const hints: Record<string, string> = {};
  for (const f of seen?.fields ?? []) hints[f.key] = coverageHint(v, f);

  const set = (next: typeof rows) => onChange({ field_filters: next.length ? next : null });
  const update = (i: number, patch: Partial<(typeof rows)[number]>) =>
    set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const blank = { field: '', operator: 'equals', value: '' };
  const where = fieldCampaigns.length
    ? `the selected ${fieldCampaigns.length === 1 ? v.one('campaign') : v.many('campaign')}`
    : `every ${v.one('campaign')}`;

  return (
    <Stack gap="xs">
      <Group justify="space-between" align="flex-end">
        <div>
          <Text size="sm" fw={600}>Rules</Text>
          <Text size="xs" c="dimmed">
            {`Fields seen on ${v.many('lead')} in ${where}. You can also type a field name.`}
          </Text>
        </div>
        <Switch
          size="xs"
          label={`Search custom fields across all ${v.many('campaign')}`}
          description={`Ignores the ${v.one('campaign')} filter. Your own ${v.one('campaign')} access still applies.`}
          checked={ignoreCampaigns}
          onChange={(e) => onChange({ ignore_campaign_filter: e.currentTarget.checked || null })}
        />
      </Group>

      {rows.map((r, i) => (
        <RuleRow
          key={i}
          rule={r}
          fieldOptions={fieldOptions}
          hints={hints}
          campaignIds={fieldCampaigns}
          def={defOf(r.field.trim())}
          operators={operatorsOf(r.field.trim(), r.operator)}
          onField={(field) => {
            // A condition the new field's type does not take goes back
            // to equals, with the value cleared.
            const ok = operatorsOf(field.trim(), '').some((o) => o.value === r.operator);
            update(i, ok ? { field } : { field, operator: 'equals', value: '' });
          }}
          onChange={(patch) => update(i, patch)}
          onCopy={() => set([...rows.slice(0, i + 1), { ...r }, ...rows.slice(i + 1)])}
          onRemove={() => set(rows.filter((_, j) => j !== i))}
          onEnter={onEnter}
        />
      ))}

      <Group>
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconPlus size={13} />}
          onClick={() => set([...rows, blank])}
        >
          Add rule
        </Button>
      </Group>
    </Stack>
  );
}

type Rule = NonNullable<LeadFilters['field_filters']>[number];

/**
 * One rule: a field picked or typed, a condition, and a value that suggests
 * what recent leads hold for that field, with counts, when the field has no
 * option list of its own and the condition compares against one value.
 */
function RuleRow({
  rule: r,
  fieldOptions,
  hints,
  campaignIds,
  def,
  operators,
  onField,
  onChange,
  onCopy,
  onRemove,
  onEnter,
}: {
  rule: Rule;
  fieldOptions: ReturnType<typeof ruleFieldOptions>;
  hints: Record<string, string>;
  campaignIds: string[];
  def: PropertyDef | null;
  operators: { value: string; label: string }[];
  onField: (field: string) => void;
  onChange: (patch: Partial<Rule>) => void;
  onCopy: () => void;
  onRemove: () => void;
  onEnter: (e: KeyboardEvent) => void;
}) {
  const field = r.field.trim();
  const valueless = VALUELESS_OPERATORS.has(r.operator);
  const [typedField] = useDebouncedValue(field, 300);
  const wantsValues = !!field && !valueless && !def?.options.length && def?.type !== 'boolean';
  const suggested = useLeadFieldValues(typedField, campaignIds, wantsValues).data?.values;
  // Picking an option puts its label in the box; the rule stores the key, so
  // the picked key wins over the label that follows.
  const picked = useRef<string | null>(null);
  return (
    <Group gap="xs" wrap="nowrap">
      <Autocomplete
        {...COMPACT}
        aria-label="Field"
        placeholder="Choose or type a field"
        data={fieldOptions}
        value={r.field}
        onOptionSubmit={(key) => { picked.current = key; }}
        onChange={(f) => {
          const key = picked.current ?? f;
          picked.current = null;
          onField(key);
        }}
        onKeyDown={onEnter}
        maxDropdownHeight={300}
        comboboxProps={{ width: 320, position: 'bottom-start' }}
        filter={filterFieldOptions}
        renderOption={({ option }) => {
          const label = (option as { label?: string }).label ?? option.value;
          // The key under the label only when the label is not just the key.
          const under = [label !== option.value ? option.value : '', hints[option.value] ?? '']
            .filter(Boolean)
            .join(' · ');
          return (
            <div>
              <Text size="xs">{label}</Text>
              {under && <Text size="xs" c="dimmed">{under}</Text>}
            </div>
          );
        }}
        data-testid="rule-field"
        style={{ flex: 1 }}
      />
      <Select
        {...COMPACT}
        aria-label="Condition"
        data={operators}
        value={r.operator}
        onChange={(v) => onChange({ operator: v ?? 'equals' })}
        allowDeselect={false}
        w={170}
      />
      <FilterValue
        {...COMPACT}
        def={def}
        operator={r.operator}
        placeholder={!field ? 'Choose a field to unlock' : valueless ? 'No value needed' : 'Value'}
        disabled={!field || valueless}
        value={valueless ? '' : (r.value ?? '')}
        onChange={(value) => onChange({ value })}
        onKeyDown={onEnter}
        suggestions={wantsValues && typedField === field ? suggested : undefined}
        style={{ flex: 1 }}
      />
      <Tooltip label="Copy rule" withArrow>
        <ActionIcon variant="subtle" aria-label="Copy rule" onClick={onCopy}>
          <IconCopy size={15} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Remove rule" withArrow>
        <ActionIcon variant="subtle" color="red" aria-label="Remove rule" onClick={onRemove}>
          <IconTrash size={15} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

export function LeadsFilterPanel({
  opened,
  filters,
  onChange,
  onSearch,
  onReset,
  onToggleAdvanced,
  pending = false,
  activeCount = 0,
  leftActions,
}: {
  /** Whether Advanced filters is open. */
  opened: boolean;
  /** The draft being edited, not what the table shows. */
  filters: LeadFilters;
  onChange: (patch: LeadFilters) => void;
  /** Apply the draft to the table. */
  onSearch: () => void;
  /** Clear the draft and the applied filters. */
  onReset: () => void;
  onToggleAdvanced: () => void;
  /** The draft has edits Search has not applied. */
  pending?: boolean;
  /** How many filters are applied, shown on the Advanced filters button. */
  activeCount?: number;
  /** Rendered after the buttons: the active-filter count and the
      unsaved-tab controls, which would otherwise each take a row. */
  leftActions?: React.ReactNode;
}) {
  const v = useVocab();
  const enums = useEnums();
  const campaigns = useCampaigns();
  const suppliers = useEntities('suppliers');
  const buyers = useEntities('buyers');
  const destinations = useDestinations();
  // 100 is the endpoint's ceiling. Past that an import is found by going to
  // the import itself, not by scrolling a filter.
  const imports = useImports(100);
  const narrow = useMediaQuery('(max-width: 36em)') ?? false;
  // Fetched once the panel is open; the tag a search arrived with is kept
  // as an option even if it is not in the list, so it always shows.
  const facets = useLeadFacets(opened || !!filters.source);
  const sourceOptions = [...new Set([...(facets.data?.sources ?? []), ...(filters.source ? [filters.source] : [])])];

  const onEnter = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearch();
    }
  };

  // Score filters, once the organisation scores (never for a scoped login).
  const scoring = useScoringInUse();
  const bands = useScoringSettings(scoring).data?.bands ?? [];

  const deliveryStatuses = (enums.data?.delivery_statuses ?? []).map((s) => ({
    value: s.value,
    label: s.label,
  }));

  return (
    <Stack gap="xs">
      {/* Always visible: the four dimensions that get used constantly, then
          the buttons. Only the filters grow; the buttons keep their width. */}
      {/* On a phone the four filters take two to a row and the buttons drop
          to a line of their own; in one row each box was a few letters wide. */}
      <Group align="flex-start" gap="xs" wrap={narrow ? 'wrap' : 'nowrap'} data-filter-toolbar>
        <SimpleGrid
          cols={narrow ? 2 : 4}
          spacing="xs"
          verticalSpacing="xs"
          style={narrow ? { flexBasis: '100%', minWidth: 0 } : { flex: 1, minWidth: 0 }}
        >
          <MultiSelect
            {...COMPACT_MULTI}
            aria-label={v.One('campaign')}
            placeholder={`All ${v.many('campaign')}`}
            searchable
            clearable
            data={campaignOptions(campaigns.data?.campaigns ?? [])}
            value={filters.campaign_ids ?? []}
            onChange={(v) => onChange({ campaign_ids: listOrNull(v) })}
          />
          <MultiSelect
            {...COMPACT_MULTI}
            aria-label={v.Many('supplier')}
            placeholder={`All ${v.many('supplier')}`}
            searchable
            clearable
            data={supplierOptions(suppliers.data?.entities ?? [])}
            value={filters.supplier_ids ?? []}
            onChange={(v) => onChange({ supplier_ids: listOrNull(v) })}
          />
          <MultiSelect
            {...COMPACT_MULTI}
            aria-label={v.Many('buyer')}
            placeholder={`All ${v.many('buyer')}`}
            searchable
            clearable
            data={groupByActive(
              buyers.data?.entities ?? [],
              (b) => ({ value: b.id, label: b.name }),
              [`Active ${v.many('buyer')}`, `Inactive ${v.many('buyer')}`],
            )}
            value={filters.buyer_ids ?? []}
            onChange={(v) => onChange({ buyer_ids: listOrNull(v) })}
          />
          <DateRangeControl filters={filters} onChange={onChange} onEnter={onEnter} />
        </SimpleGrid>

        {/* Icons and one labelled button: these used to be four labelled
            buttons and two badges, about a third of the row, which squeezed
            the filters. The applied count rides on the filters icon and the
            unsearched-changes dot on Search. */}
        <Group gap={6} wrap="nowrap" align="flex-start" style={narrow ? { marginLeft: 'auto' } : undefined}>
          <Tooltip label={opened ? 'Hide advanced filters' : 'Advanced filters'} withArrow>
            <Indicator
              label={activeCount}
              disabled={activeCount === 0}
              size={15}
              offset={3}
            >
              <Button
                variant={opened ? 'light' : 'default'}
                size="xs"
                px={8}
                onClick={onToggleAdvanced}
                aria-expanded={opened}
                aria-label="Advanced filters"
                rightSection={
                  <IconChevronDown
                    size={12}
                    style={{ transform: opened ? 'rotate(180deg)' : undefined }}
                  />
                }
              >
                <IconAdjustmentsHorizontal size={15} />
              </Button>
            </Indicator>
          </Tooltip>
          <Tooltip label="Reset filters" withArrow>
            <ActionIcon variant="default" size={30} onClick={onReset} aria-label="Reset filters">
              <IconRefresh size={15} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Press Search to apply your changes" withArrow disabled={!pending}>
            <Indicator color="orange" size={9} offset={2} disabled={!pending} processing>
              <Button
                size="xs"
                px={10}
                leftSection={<IconSearch size={14} />}
                onClick={onSearch}
                aria-label={pending ? 'Search (changes not applied yet)' : 'Search'}
              >
                Search
              </Button>
            </Indicator>
          </Tooltip>
          {leftActions}
        </Group>
      </Group>

      <Collapse in={opened}>
        <Stack gap="sm" pt="xs">
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xs" verticalSpacing="xs">
            <MultiSelect
              {...COMPACT_MULTI}
              label="Import"
              placeholder="Any"
              searchable
              clearable
              data={(imports.data?.imports ?? []).map((j) => ({
                value: j.job_id,
                label: importLabel(j),
              }))}
              value={filters.import_ids ?? []}
              onChange={(v) => onChange({ import_ids: listOrNull(v) })}
            />
            <MultiSelect
              {...COMPACT_MULTI}
              label="Capture method"
              placeholder="Any"
              clearable
              data={[
                { value: 'api', label: `API ${v.many('lead')}` },
                { value: 'import', label: `Imported ${v.many('lead')}` },
              ]}
              value={filters.capture_methods ?? []}
              onChange={(v) => onChange({ capture_methods: listOrNull(v) })}
            />
            <Select
              size="xs"
              label={v.p.sourceTag}
              placeholder="Any"
              searchable
              clearable
              data={sourceOptions}
              value={filters.source ?? null}
              onChange={(s) => onChange({ source: s ?? null })}
            />
            <MultiSelect
              {...COMPACT_MULTI}
              label={<InfoLabel help={`${v.p.Sold} ${v.p.soldPrep}, or tried against, these deliveries`}>Delivery name</InfoLabel>}
              placeholder="Any"
              searchable
              clearable
              data={groupByActive(
                destinations.data?.destinations ?? [],
                (d) => ({
                  value: d.id,
                  label: d.buyer_name ? `${d.name} · ${d.buyer_name}` : d.name,
                }),
                ['Active deliveries', 'Inactive deliveries'],
              )}
              value={filters.destination_ids ?? []}
              onChange={(v) => onChange({ destination_ids: listOrNull(v) })}
            />
            <MultiSelect
              {...COMPACT_MULTI}
              label="Delivery status"
              placeholder="Any"
              clearable
              data={deliveryStatuses}
              value={filters.delivery_statuses ?? []}
              onChange={(v) => onChange({ delivery_statuses: listOrNull(v) })}
            />
            <MultiSelect
              {...COMPACT_MULTI}
              label={`${v.p.Sold} status`}
              placeholder="Any"
              clearable
              data={[
                // 'not_sold', not 'unsold'. The server maps these tokens to
                // SQL, and an unknown one used to be dropped, which turned
                // the whole filter into a no-op.
                { value: 'sold', label: v.p.Sold },
                { value: 'not_sold', label: v.p.Unsold },
                { value: 'returned', label: 'Returned' },
              ]}
              value={filters.sold_statuses ?? []}
              onChange={(v) => onChange({ sold_statuses: listOrNull(v) })}
            />
            <MultiSelect
              {...COMPACT_MULTI}
              label={<InfoLabel help={`What the mobile networks said about the number. Not checked means the ${v.one('campaign')} does not check numbers.`}>Phone check</InfoLabel>}
              placeholder="Any"
              clearable
              data={HLR_STATUSES}
              value={filters.hlr_statuses ?? []}
              onChange={(v) => onChange({ hlr_statuses: listOrNull(v) })}
            />
            <MultiSelect
              {...COMPACT_MULTI}
              label={<InfoLabel help="Redistributions are recorded from 15 May 2026">{`${v.One('lead')} activity`}</InfoLabel>}
              placeholder="Any"
              clearable
              data={[
                { value: 'returned', label: 'Returned' },
                { value: 'redistributed', label: 'Redistributed' },
              ]}
              value={filters.lead_activities ?? []}
              onChange={(v) => onChange({ lead_activities: listOrNull(v) })}
            />
            {scoring && (
              <Select
                size="xs"
                label={<InfoLabel help={`The band the ${v.one('lead')}'s score fell in when it arrived`}>Score band</InfoLabel>}
                placeholder="Any"
                clearable
                data={bands.map((b) => ({ value: b.key, label: b.label }))}
                value={filters.score_band ?? null}
                onChange={(b) => onChange({ score_band: b ?? null })}
                data-testid="filter-score-band"
              />
            )}
            {scoring && (
              <Group gap={6} wrap="nowrap" align="flex-end">
                <NumberInput
                  size="xs"
                  label="Score from"
                  placeholder="Any"
                  allowDecimal={false}
                  value={filters.min_score ?? ''}
                  onChange={(n) => onChange({ min_score: typeof n === 'number' ? n : null })}
                  onKeyDown={onEnter}
                  data-testid="filter-min-score"
                  style={{ flex: 1 }}
                />
                <NumberInput
                  size="xs"
                  label="to"
                  placeholder="Any"
                  allowDecimal={false}
                  value={filters.max_score ?? ''}
                  onChange={(n) => onChange({ max_score: typeof n === 'number' ? n : null })}
                  onKeyDown={onEnter}
                  data-testid="filter-max-score"
                  style={{ flex: 1 }}
                />
              </Group>
            )}
          </SimpleGrid>

          <RuleBuilder filters={filters} onChange={onChange} onEnter={onEnter} enabled={opened} />
        </Stack>
      </Collapse>
    </Stack>
  );
}
